// @/shared/hooks/useFeatures.ts
import { useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FeatureConfig, UseFeaturesProps, ComponentConfig } from "@/shared/types/Features";
import { FeatureUtils } from "@/shared/utils/FeatureUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { useDeepCompareMemo } from "@react-hookz/web";

const EMPTY_FEATURE_CONFIG: FeatureConfig = { features: {} };

export const useFeatures = ({ formId, quote, enabled = true }: UseFeaturesProps) => {
    const queryClient = useQueryClient();
    const searchParams = useSearchParams();
    const currentParams = useMemo(() => {
        return Object.fromEntries(searchParams.entries());
    }, [searchParams]);

    // URL pcode wins over the stored quote code ("last pcode wins"), matching formPage's
    // server-side resolution so the SSR-seeded cache key and this one can never diverge.
    const urlPcode = useMemo(() => UIUtils.getCaseInsensitiveValue(new URLSearchParams(currentParams), "pcode"), [currentParams]);

    const normalizedPriorityCode = FeatureUtils.resolveNormalizedPriorityCode({ quote, urlPcode });
    const queryKey = FeatureUtils.generateQueryKey(formId, quote || { discountCode: normalizedPriorityCode }, urlPcode);

    const featuresQuery = useQuery({
        queryKey,
        queryFn: async (): Promise<FeatureConfig> => {
            const quoteForApi = quote || { discountCode: normalizedPriorityCode };
            return FeatureUtils.fetchFeatureConfig(formId, quoteForApi, "", currentParams);
        },
        enabled: enabled && !!normalizedPriorityCode && normalizedPriorityCode !== "spot",
        staleTime: 1000 * 60 * 60,
        retry: 1,
        initialData: () => {
            return queryClient.getQueryData(queryKey);
        }
    });

    const memoizedFeaturesQuery = useDeepCompareMemo(() => featuresQuery, [featuresQuery]);

    return useMemo(
        () => ({
            featuresQuery: memoizedFeaturesQuery,
            featureConfig: memoizedFeaturesQuery.data || EMPTY_FEATURE_CONFIG,
            appConfig: memoizedFeaturesQuery.data?.appConfig,
            isLoading: memoizedFeaturesQuery.isLoading,
            isError: memoizedFeaturesQuery.isError,
            error: memoizedFeaturesQuery.error,
            hasFeatures: (stepId: string): boolean => {
                const stepConfig = memoizedFeaturesQuery.data?.features?.[stepId];
                return !!stepConfig?.components?.length;
            },
            getStepComponents: (stepId: string): ComponentConfig[] => {
                return memoizedFeaturesQuery.data?.features?.[stepId]?.components || [];
            },
            getComponentMetadata: (stepId: string, componentId: string): Record<string, any> | undefined => {
                const components = memoizedFeaturesQuery.data?.features?.[stepId]?.components || [];
                const component = components.find(comp => comp.id === componentId);
                return component?.metadata;
            }
        }),
        [memoizedFeaturesQuery]
    );
};
